// src/components/table/columnsConfig/commonUtils.js

export const getCommonHeadCellProps = (theme) => ({
  sx: {
    padding: {
      xs: theme.spacing(0.5, 0.25, 0.5, 0.5),
      sm: theme.spacing(0.55, 0.55, 0.55, 0.55),
    },
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.3px",
    borderBottom: `2px solid ${theme.palette.divider}`,
    verticalAlign: "middle",
    lineHeight: "1.1",
    boxSizing: "border-box",
  },
});

export const getCommonBodyCellProps = (theme) => ({
  sx: {
    padding: {
      xs: theme.spacing(0.5, 0.25, 0.5, 0.5),
      sm: theme.spacing(0.55, 0.55, 0.55, 0.55),
    },
    color: theme.palette.text.primary,
    fontSize: {
      xs: "0.65rem",
      sm: "0.75rem",
    },
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.default,
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
});
export const getCommonBodyCellPropsTradesPositions = (theme) => ({
  sx: {
    padding: {
      xs: theme.spacing(0.5, 0.25, 0.5, 0.5),
      sm: theme.spacing(0.55, 0.55, 0.55, 0.55),
    },
    color: theme.palette.text.primary,
    fontSize: {
      xs: "0.65rem",
      sm: "0.75rem",
    },
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxSizing: "border-box",
  },
});

// Updated utility function for ECD status styling - now just text color, no background
export const getEcdStatusCellProps = (theme) => ({
  sx: {
    padding: {
      xs: theme.spacing(0.5, 0.25, 0.5, 0.5),
      sm: theme.spacing(0.55, 0.55, 0.55, 0.55),
    },
    color: theme.palette.text.primary,
    fontSize: {
      xs: "0.65rem",
      sm: "0.75rem",
    },
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxSizing: "border-box",
    textAlign: "center",
    fontWeight: 600,
  },
});

// Helper function to get sort priority for ECD statuses
export const getEcdStatusSortPriority = (status) => {
  if (!status) return 999; // Put empty/null values at the end
  
  const statusText = String(status).toLowerCase();
  
  if (statusText === "current_process_no_match") {
    return 1; // "Review: ECD not found today" - highest priority
  } else if (statusText === "no_match") {
    return 2; // "Import failed at T-1" - second priority
  } else if (statusText === "match") {
    return 3; // "Import successful at T-1" - third priority
  } else {
    return 999; // Unknown statuses at the end
  }
};

// Updated helper function to render ECD status with text colors only (no background)
export const renderEcdStatus = (status, theme) => {
  if (!status) return "-";
  
  const statusText = String(status);
  let textColor;
  let displayText;
  
  if (statusText.toLowerCase() === "match") {
    textColor = theme.palette.text.primary; // Same as positive numbers (default text color)
    displayText = "Import successful at T-1";
  } else if (statusText.toLowerCase() === "current_process_no_match") {
    textColor = theme.palette.error.main; // Same as negative numbers (red)
    displayText = "Review: ECD not found today";
  } else if (statusText.toLowerCase() === "no_match") {
    textColor = theme.palette.error.main; // Same as negative numbers (red)
    displayText = "Import failed at T-1";
  } else {
    // Default styling for unknown statuses
    textColor = theme.palette.text.primary;
    displayText = statusText;
  }
  
  return (
    <span
      style={{
        color: textColor,
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    >
      {displayText}
    </span>
  );
};

export const getCommonFooterCellProps = (theme) => ({
  sx: {
    padding: {
      xs: theme.spacing(0.5, 0.25, 0.5, 0.5),
      sm: theme.spacing(0.55, 0.55, 0.55, 0.55),
    },
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: {
      xs: "0.65rem",
      sm: "0.75rem",
    },
    boxSizing: "border-box",
  },
});
// Columns are flex weights, not fixed widths: they share the table width in
// proportion to `size`, shrink to MIN_SIZE_RATIO of it, and only then scroll.
// Requires layoutMode "grid" (set in TableContainer).
const MIN_SIZE_RATIO = 0.35;
const MIN_COLUMN_WIDTH = 48;

const fitWidthStyles = (size) => {
  const minWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(size * MIN_SIZE_RATIO));
  return {
    width: "auto",
    maxWidth: "none",
    minWidth,
    flex: `${size} 1 ${minWidth}px`,
  };
};

export const createColumn = (
  commonHeadCellProps,
  commonBodyCellProps,
  commonFooterCellProps,
  accessorKey,
  header,
  size,
  align = "right",
  overrides = {},
) => ({
  accessorKey,
  header,
  size,
  minSize: MIN_COLUMN_WIDTH,
  muiTableHeadCellProps: {
    ...commonHeadCellProps,
    align,
    sx: {
      ...commonHeadCellProps.sx,
      ...fitWidthStyles(size),
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "normal",
      "& .Mui-TableHeadCell-Content-Labels": { whiteSpace: "normal" },
      "& .MuiBadge-root, & .Mui-TableHeadCell-Content-Wrapper": {
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
      },
      ...overrides.head,
    },
  },
  muiTableBodyCellProps: {
    ...commonBodyCellProps,
    align,
    sx: {
      ...commonBodyCellProps.sx,
      ...fitWidthStyles(size),
      ...overrides.body,
    },
  },
  muiTableFooterCellProps: {
    ...commonFooterCellProps,
    align,
    sx: {
      ...commonFooterCellProps.sx,
      ...fitWidthStyles(size),
      ...overrides.footer,
    },
  },
  ...overrides.other,
});

export const formatNumericCell = (
  value,
  formatFn,
  negativeColor,
  positiveColor,
) => {
  if (!value || value === 0) return "-";
  const roundedValue = Math.round(value);
  const formattedValue = formatFn(roundedValue);
  return (
    <span
      style={{
        color: roundedValue < 0 ? negativeColor : positiveColor,
        fontWeight: roundedValue < 0 ? 600 : 400,
        fontSize: "12px",
      }}
    >
      {formattedValue}
    </span>
  );
};

export const formatPriceCell = (value, decimals = 4) => {
  if (!value || value === 0) return "-";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  }).format(value);
};

export const formatAggregatedFooter = (
  table,
  columnId,
  formatCurrency,
  negativeColor,
  positiveColor,
) => {
  const total = table
    .getFilteredRowModel()
    .rows.reduce((sum, row) => sum + (row.getValue(columnId) || 0), 0);
  if (!total || total === 0) return "-";
  const roundedTotal = Math.round(total);
  const formattedTotal = formatCurrency(roundedTotal);
  return (
    <span
      style={{
        color: roundedTotal < 0 ? negativeColor : positiveColor,
        fontWeight: 600,
        fontSize: "12px",
        px: 0,
        mx: 0,
      }}
    >
      {formattedTotal}
    </span>
  );
};

export const formatPercentageCell = (
  value,
  decimals = 2,
  suffix = "%",
  negativeColor = "#d32f2f", // fallback red
  positiveColor = "#212121", // fallback dark text
) => {
  if (value === null || value === undefined || isNaN(value)) return "-";
  const rounded = Number(value).toFixed(decimals);
  const color = Number(rounded) < 0 ? negativeColor : positiveColor;
  const fontWeight = Number(rounded) < 0 ? 600 : 400;
  return (
    <span style={{ color, fontWeight }}>
      {Number(rounded) > 0 ? `+${rounded}` : rounded}
      {suffix}
    </span>
  );
};
